import { createContext, useContext, useMemo } from 'react';

/**
 * The calculated fields a recruiter has typed over, and the ones they have
 * handed back, collected while they fill the form so the save can tell the
 * server which is which.
 *
 * The server keeps an override only for a field it has been told about
 * (`detach_fields`), and drops one only for a field it has been told about
 * (`reattach_fields`). It cannot work either list out for itself: the form posts
 * every field back, including the calculated one, so "incoming value differs
 * from what the formula computes" reads an edit that moved an input as an
 * override and freezes the field on the very save that should move it. Nor can
 * a cleared box be read off the payload — the form puts the computed value back
 * as soon as the formula answers, so nothing empty survives to the save.
 *
 * Nor can the submit handler work it out by comparing the box to the computed
 * value: `useCalculatedValue` debounces, so a quick Save compares against a
 * result that has not arrived. Only CalculatedFieldSync knows a human typed
 * there, so it reports it here.
 *
 * Plain Sets held by a memo rather than state: nothing renders off this, and a
 * Set in state would re-render every field in the form on the first keystroke of
 * an override.
 */
export interface OverriddenFieldsStore {
  mark: (handle: string) => void;
  unmark: (handle: string) => void;
  // Read at submit. Plain arrays so the payload can carry them as-is.
  handles: () => string[];
  returned: () => string[];
}

// The context itself rather than a Provider component, so this file exports no
// component and can stay a plain module beside the hooks.
export const OverriddenFieldsContext =
  createContext<OverriddenFieldsStore | null>(null);

/**
 * Null outside a provider, which is every other form that renders a calculated
 * field — requisitions, the job form, the settings preview. A take-over is an
 * offer's idea, and those forms post no `detach_fields`, so reporting one
 * there would go nowhere.
 */
export const useOverriddenFields = () => useContext(OverriddenFieldsContext);

/**
 * `key` starts a fresh store whenever the drawer is pointed at another offer:
 * the sets live on a ref for as long as the drawer is mounted, and a container
 * that is reused across offers would otherwise send offer A's handles with
 * offer B's save.
 */
export const useOverriddenFieldsStore = (
  key?: string | number
): OverriddenFieldsStore =>
  // The sets are built INSIDE the memo rather than reset through a ref: a memo
  // is a cache React may recompute and StrictMode double-invokes, so resetting
  // something outside it is a side effect in the wrong place. Rebuilt per key,
  // the new store simply starts empty.
  useMemo(() => {
    const held = new Set<string>();
    const handedBack = new Set<string>();

    return {
      mark: (handle: string) => {
        held.add(handle);
        // A field cannot be both taken over and handed back on one save, and
        // the last thing the recruiter did is the true one.
        handedBack.delete(handle);
      },
      unmark: (handle: string) => {
        held.delete(handle);
        handedBack.add(handle);
      },
      handles: () => Array.from(held),
      returned: () => Array.from(handedBack),
    };
  }, [key]);
